import {
  BarChartIcon,
  ExclamationTriangleIcon,
  EyeOpenIcon,
  KeyboardIcon,
  QuestionMarkCircledIcon,
  VideoIcon,
} from '@radix-ui/react-icons';
import {
  BaseballIcon,
  HelmetIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { SectionName, SubSectionName } from 'enums/route.enums';
import { ISection } from 'interfaces/i-section';
import { IUserRoleFeatureFlags } from 'interfaces/i-user-feature-flags';
import { ICombinedPermissions } from 'lib_ts/interfaces/i-permissions';
import { paths } from 'routes/paths';
import { segments } from 'routes/segments';

/**
 * Catalogs this app's nav sections and subsections.
 * See useNavSections for usage.
 *
 * Route strings are defined in routes/paths.ts
 */
export class SectionsHelper {
  private static INVISIBLE_SECTIONS: ISection[] = [
    {
      value: SectionName.GameInProgress,
      slug: `/${segments.gameInProgress}`,
      to: paths.gameInProgress,
      label: 'main.game-in-progress',
      icon: ExclamationTriangleIcon,
      invisible: true,
    },
    {
      value: SectionName.NotFound,
      slug: `/${segments.notFound}`,
      to: paths.notFound,
      label: 'main.not-found',
      icon: QuestionMarkCircledIcon,
      invisible: true,
    },
    {
      value: SectionName.Unavailable,
      slug: `/${segments.unavailable}`,
      to: paths.unavailable,
      label: 'main.unavailable',
      icon: QuestionMarkCircledIcon,
      invisible: true,
    },
  ];

  static getAdminSections(flags: IUserRoleFeatureFlags): ISection[] {
    const { sections } = flags;

    return [
      {
        value: SectionName.AdminPortal,
        slug: `/${segments.admin}`,
        to: paths.admin.default,
        label: 'main.admin-portal',
        icon: KeyboardIcon,
        invisible: !sections.adminPortal,
        subsections: [
          {
            default: true,
            value: SubSectionName.Machines,
            slug: `/${segments.machines}`,
            to: paths.admin.machines,
            label: 'admin.machines',
          },
          {
            value: SubSectionName.Users,
            slug: `/${segments.users}`,
            to: paths.admin.users,
            label: 'admin.users',
          },
          {
            value: SubSectionName.Organizations,
            slug: `/${segments.organizations}`,
            to: paths.admin.organizations,
            label: 'admin.organizations',
            invisible: !sections.adminTeams,
            icon: SuperAdminIcon,
          },
          {
            value: SubSectionName.BallTypes,
            slug: `/${segments.ballTypes}`,
            to: paths.admin.ballTypes,
            label: 'admin.ball-types',
            invisible: !sections.adminBallTypes,
            icon: SuperAdminIcon,
          },
          {
            value: SubSectionName.MachineModels,
            slug: `/${segments.machineModels}`,
            to: paths.admin.machineModels,
            label: 'admin.machine-models',
            invisible: !sections.adminMachineModels,
            icon: SuperAdminIcon,
          },
          {
            value: SubSectionName.ErrorTypes,
            slug: `/${segments.errorTypes}`,
            to: paths.admin.errorTypes,
            label: 'admin.error-types',
            invisible: !sections.adminErrorTypes,
            icon: SuperAdminIcon,
          },
          {
            value: SubSectionName.StaticVideos,
            slug: `/${segments.staticVideos}`,
            to: paths.admin.staticVideos,
            label: 'admin.videos',
            invisible: !sections.adminStaticVideos,
            icon: SuperAdminIcon,
          },

          {
            value: SubSectionName.ApiKeys,
            slug: `/${segments.apiKeys}`,
            to: paths.admin.apiKeys,
            label: 'admin.api-keys',
            invisible: !sections.adminApiKeys,
          },
          {
            value: SubSectionName.ServerErrors,
            slug: `/${segments.serverErrors}`,
            to: paths.admin.serverErrors,
            label: 'admin.server-errors',
            invisible: !sections.adminServerErrors,
            icon: SuperAdminIcon,
          },
          {
            value: SubSectionName.ServerControls,
            slug: `/${segments.serverControls}`,
            to: paths.admin.serverControls,
            label: 'admin.server-controls',
            invisible: !sections.adminServerControls,
            icon: SuperAdminIcon,
          },
        ],
      },
      {
        value: SectionName.FeatureDemo,
        slug: `/${segments.featureDemo}`,
        to: paths.featureDemo,
        label: 'main.feature-demo',
        icon: EyeOpenIcon,
        invisible: !sections.featureDemo,
      },
    ];
  }

  static getUserSections(config: {
    flags: IUserRoleFeatureFlags;
    permissions: ICombinedPermissions;
    isInGame: boolean;
  }): ISection[] {
    const { sections } = config.flags;

    return [
      ...this.INVISIBLE_SECTIONS,
      {
        value: SectionName.Pitches,
        slug: `/${segments.pitches}`,
        to: paths.pitches.lists,
        label: 'main.pitches',
        icon: BaseballIcon,
        subsections: [
          {
            default: true,
            value: SubSectionName.Lists,
            slug: `/${segments.lists}`,
            to: paths.pitches.lists,
            label: 'main.pitch-lists',
          },
          {
            value: SubSectionName.Library,
            slug: `/${segments.library}`,
            to: paths.pitches.library,
            label: 'main.library',
          },
          {
            value: SubSectionName.GameData,
            slug: `/${segments.gameData}`,
            to: paths.pitches.gameData.root,
            label: 'main.game-data',
            invisible: !config.permissions.mlb_stats_api || config.isInGame,
          },
          // not shown in selector
          {
            value: SubSectionName.Design,
            slug: `/${segments.design}`,
            to: paths.pitches.design(),
            label: 'main.pitch-design',
            invisible: true,
          },

          {
            value: SubSectionName.List,
            slug: `/${segments.list}`,
            // param route; base path for matching / rare nav without an id
            to: `${paths.pitches.root}/${segments.list}`,
            label: 'main.pitch-list',
            invisible: true,
          },
          {
            value: SubSectionName.Upload,
            slug: `/${segments.upload}`,
            to: paths.pitches.upload,
            label: 'main.bulk-pitch-upload',
            invisible: true,
          },
        ],
      },
      {
        value: SectionName.HitterLibrary,
        slug: `/${segments.hitterLibrary}`,
        to: paths.hitterLibrary.hitters,
        label: 'main.hitters',
        icon: HelmetIcon,
        invisible: !sections.hitterLibrary,
        subsections: [
          {
            default: true,
            value: SubSectionName.Hitters,
            slug: `/${segments.hitters}`,
            to: paths.hitterLibrary.hitters,
            label: 'main.hitters',
          },
          {
            value: SubSectionName.HitterProfile,
            slug: `/${segments.hitterProfile}`,
            to: `${paths.hitterLibrary.root}/${segments.hitterProfile}`,
            label: 'main.hitter-profile',
          },
          {
            value: SubSectionName.HitterSession,
            slug: `/${segments.hitterSession}`,
            to: `${paths.hitterLibrary.root}/${segments.hitterSession}`,
            label: 'main.hitter-session',
          },
        ],
      },
      {
        value: SectionName.VideoLibrary,
        slug: `/${segments.videoLibrary}`,
        to: paths.videoLibrary,
        label: 'main.videos',
        icon: VideoIcon,
        invisible: !sections.videoLibrary,
      },
      {
        value: SectionName.Analytics,
        slug: `/${segments.analytics}`,
        to: paths.analytics.hittingReports,
        label: 'main.analytics',
        icon: BarChartIcon,
        invisible: !sections.analytics || config.isInGame,
        subsections: [
          {
            default: true,
            value: SubSectionName.HittingReports,
            slug: `/${segments.hittingReports}`,
            to: paths.analytics.hittingReports,
            label: 'main.hitting-reports',
          },
          {
            value: SubSectionName.SessionHistory,
            slug: `/${segments.sessionHistory}`,
            to: paths.analytics.sessionHistory,
            label: 'main.session-history',
          },
          // not shown in selector
          {
            value: SubSectionName.HitterSession,
            slug: `/${segments.hitterSession}`,
            to: `${paths.analytics.root}/${segments.hitterSession}`,
            label: 'main.hitter-session',
            invisible: true,
          },
        ],
      },
    ];
  }
}
